/**
 * 606 Meta5 AI Trading - Backtest Tab
 */
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FlaskConical, Play, Loader2 } from 'lucide-react';
import { useTrading } from '../../lib/trading-i18n';
import { runBacktest } from '../../lib/trading-api';
import PnL from '../../components/trading/PnL';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const BacktestTab = ({ language = 'ar' }) => {
  const t = useTrading(language);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  
  const [params, setParams] = useState({
    symbol: 'EURUSD',
    start_balance: 10000,
    risk_per_trade_pct: 1,
    risk_reward_ratio: 2,
    ema_fast: 9,
    ema_slow: 21,
    rsi_period: 14,
    rsi_overbought: 70,
    rsi_oversold: 30,
    bars: 500,
    seed: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const submitParams = {
        ...params,
        seed: params.seed ? parseInt(params.seed) : null
      };
      const data = await runBacktest(submitParams);
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      {/* Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/10 p-6"
      >
        <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
          <FlaskConical className="h-5 w-5 text-[#d4af37]" />
          {t.backtest}
        </h3>

        <form onSubmit={handleSubmit} className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm text-[#71717A] mb-1">{t.symbol}</label>
            <Select value={params.symbol} onValueChange={(v) => setParams({ ...params, symbol: v })}>
              <SelectTrigger className="bg-white/5 border-white/10 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="EURUSD">EURUSD</SelectItem>
                <SelectItem value="GBPUSD">GBPUSD</SelectItem>
                <SelectItem value="USDJPY">USDJPY</SelectItem>
                <SelectItem value="XAUUSD">XAUUSD</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm text-[#71717A] mb-1">{t.startBalance}</label>
            <Input
              type="number"
              value={params.start_balance}
              onChange={(e) => setParams({ ...params, start_balance: parseFloat(e.target.value) })}
              className="bg-white/5 border-white/10 text-white"
            />
          </div>

          <div>
            <label className="block text-sm text-[#71717A] mb-1">{t.riskPerTrade}</label>
            <Input
              type="number"
              step="0.1"
              value={params.risk_per_trade_pct}
              onChange={(e) => setParams({ ...params, risk_per_trade_pct: parseFloat(e.target.value) })}
              className="bg-white/5 border-white/10 text-white"
            />
          </div>

          <div>
            <label className="block text-sm text-[#71717A] mb-1">{t.riskReward}</label>
            <Input
              type="number"
              step="0.1"
              value={params.risk_reward_ratio}
              onChange={(e) => setParams({ ...params, risk_reward_ratio: parseFloat(e.target.value) })}
              className="bg-white/5 border-white/10 text-white"
            />
          </div>

          <div>
            <label className="block text-sm text-[#71717A] mb-1">{t.emaFast}</label>
            <Input
              type="number"
              value={params.ema_fast}
              onChange={(e) => setParams({ ...params, ema_fast: parseInt(e.target.value) })}
              className="bg-white/5 border-white/10 text-white"
            />
          </div>

          <div>
            <label className="block text-sm text-[#71717A] mb-1">{t.emaSlow}</label>
            <Input
              type="number"
              value={params.ema_slow}
              onChange={(e) => setParams({ ...params, ema_slow: parseInt(e.target.value) })}
              className="bg-white/5 border-white/10 text-white"
            />
          </div>

          <div>
            <label className="block text-sm text-[#71717A] mb-1">{t.rsiPeriod}</label>
            <Input
              type="number"
              value={params.rsi_period}
              onChange={(e) => setParams({ ...params, rsi_period: parseInt(e.target.value) })}
              className="bg-white/5 border-white/10 text-white"
            />
          </div>

          <div>
            <label className="block text-sm text-[#71717A] mb-1">{t.bars}</label>
            <Input
              type="number"
              value={params.bars}
              onChange={(e) => setParams({ ...params, bars: parseInt(e.target.value) })}
              className="bg-white/5 border-white/10 text-white"
            />
          </div>

          <div>
            <label className="block text-sm text-[#71717A] mb-1">{t.rsiOverbought}</label>
            <Input
              type="number"
              value={params.rsi_overbought}
              onChange={(e) => setParams({ ...params, rsi_overbought: parseInt(e.target.value) })}
              className="bg-white/5 border-white/10 text-white"
            />
          </div>

          <div>
            <label className="block text-sm text-[#71717A] mb-1">{t.rsiOversold}</label>
            <Input
              type="number"
              value={params.rsi_oversold}
              onChange={(e) => setParams({ ...params, rsi_oversold: parseInt(e.target.value) })}
              className="bg-white/5 border-white/10 text-white"
            />
          </div>

          <div>
            <label className="block text-sm text-[#71717A] mb-1">{t.seed}</label>
            <Input
              type="number"
              value={params.seed}
              onChange={(e) => setParams({ ...params, seed: e.target.value })}
              placeholder="Random"
              className="bg-white/5 border-white/10 text-white"
            />
          </div>

          <div className="flex items-end">
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#d4af37] hover:bg-[#c9a227] text-black"
              data-testid="run-backtest-btn"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              {t.runBacktest}
            </Button>
          </div>
        </form>

        {error && (
          <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}
      </motion.div>

      {/* Results */}
      {result && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/10 p-6"
        >
          <h3 className="text-lg font-semibold text-white mb-6">{t.backtestResults}</h3>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white/[0.02] rounded-lg p-3">
              <span className="text-[#71717A] text-sm">{t.endBalance}</span>
              <div className="text-xl font-bold text-white mt-1">${result.end_balance.toLocaleString()}</div>
            </div>
            <div className="bg-white/[0.02] rounded-lg p-3">
              <span className="text-[#71717A] text-sm">{t.netPnl}</span>
              <div className="mt-1">
                <PnL value={result.net_pnl} size="xl" />
                <span className="text-xs text-[#71717A] ml-1">({result.net_pnl_pct}%)</span>
              </div>
            </div>
            <div className="bg-white/[0.02] rounded-lg p-3">
              <span className="text-[#71717A] text-sm">{t.winRate}</span>
              <div className="text-xl font-bold text-white mt-1">{result.win_rate}%</div>
            </div>
            <div className="bg-white/[0.02] rounded-lg p-3">
              <span className="text-[#71717A] text-sm">{t.maxDrawdown}</span>
              <div className="text-xl font-bold text-red-400 mt-1">{result.max_drawdown_pct}%</div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center">
              <span className="text-[#71717A] text-sm">{t.totalTrades}</span>
              <div className="text-lg font-bold text-white">{result.total_trades}</div>
            </div>
            <div className="text-center">
              <span className="text-[#71717A] text-sm">{t.wins}</span>
              <div className="text-lg font-bold text-emerald-400">{result.wins}</div>
            </div>
            <div className="text-center">
              <span className="text-[#71717A] text-sm">{t.losses}</span>
              <div className="text-lg font-bold text-red-400">{result.losses}</div>
            </div>
            <div className="text-center">
              <span className="text-[#71717A] text-sm">{t.profitFactor}</span>
              <div className="text-lg font-bold text-white">{result.profit_factor}</div>
            </div>
          </div>

          {/* Equity Curve */}
          {result.equity_curve.length > 0 && (
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={result.equity_curve}>
                  <XAxis dataKey="time" hide />
                  <YAxis domain={['auto', 'auto']} hide />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1a1a2e',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px'
                    }}
                    formatter={(value) => [`$${value.toFixed(2)}`, 'Equity']}
                  />
                  <Line
                    type="monotone"
                    dataKey="equity"
                    stroke="#d4af37"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default BacktestTab;
